import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

import { LoadingIndicator, Swiper } from '@mssgme/ui';
import { LightBox } from '../LightBox';
import { ServicePhoto } from './ServicePhoto';

import styles from './ServiceContent.scss';

const lazy = {
    loadPrevNext: true,
    preloaderClass: styles.imagePreloader,
    loadedClass: styles.loaded,
};

export const ServicePhotos = ({ photos, title, isActive, isPrev, isNext, themeStyles, style, onClose }) => {
    const navigationPrevRef = React.useRef(null);
    const navigationNextRef = React.useRef(null);
    const paginationElRef = useRef(null);
    const [shouldSwiperLoad, setShouldSwiperLoad] = useState(false);
    const [swiper, setSwiper] = useState(null);
    const [isLightbox, setIsLightbox] = useState(false);
    const [currentLightBoxSlideIndex, setCurrentLightBoxSlideIndex] = useState(0);

    const hasMultiplePhotos = photos.length > 1;
    const hasNavElements = !!paginationElRef.current && !!navigationPrevRef.current && !!navigationNextRef.current;
    const showSwiper = shouldSwiperLoad && (!hasMultiplePhotos || hasNavElements);
    const isSwipeable = isActive || isPrev || isNext;

    const openLightBox = useCallback(() => setIsLightbox(true), []);
    const closeLightBox = useCallback(() => setIsLightbox(false), []);

    const handleSlide = useCallback((index) => {
        setCurrentLightBoxSlideIndex(index);
        openLightBox();
    });

    const pagination = useMemo(
        () =>
            hasMultiplePhotos && {
                el: paginationElRef.current,
                clickable: true,
                renderBullet: (index, className) => {
                    return `<div class="${className} ${styles.dot}"><div style="background-color: ${
                        themeStyles ? themeStyles.color : 'null'
                    }"></div></div>`;
                },
                clickableClass: styles.clickablePagination,
                bulletActiveClass: styles.activeBullet,
            },
        [paginationElRef.current, hasMultiplePhotos, themeStyles]
    );

    const handleLoad = useCallback(() => void (swiper && swiper.updateAutoHeight()), [swiper]);

    useEffect(() => {
        if (isSwipeable) {
            setShouldSwiperLoad(true);
        }
    }, [isSwipeable]);

    return (
        <div className={styles.swiperContainer}>
            {showSwiper ? (
                <Swiper
                    className={styles.swiper}
                    nested
                    autoHeight
                    lazy={lazy}
                    pagination={pagination}
                    navigation={{
                        prevEl: navigationPrevRef.current,
                        nextEl: navigationNextRef.current,
                    }}
                    speed={600}
                    watchOverflow={hasMultiplePhotos}
                    loop={hasMultiplePhotos}
                    onSwiper={setSwiper}
                >
                    {photos.map((photoUrl, index) => (
                        <ServicePhoto
                            key={photoUrl + index}
                            index={index}
                            photoUrl={photoUrl}
                            title={title}
                            style={style}
                            onLoad={handleLoad}
                            onClick={handleSlide}
                        />
                    ))}
                </Swiper>
            ) : (
                <div className={styles.preloader}>
                    <div>
                        <LoadingIndicator />
                    </div>
                </div>
            )}
            {hasMultiplePhotos && (
                <div className={cn(styles.controlsContainer, { [styles.hidden]: !showSwiper })}>
                    <div className={cn(styles.controls, styles.noSwiping)} style={style.pagination}>
                        <div className={styles.control} ref={navigationPrevRef}>
                            <FiChevronLeft size={16} />
                        </div>
                        <div className={styles.pagination} ref={paginationElRef} />
                        <div className={styles.control} ref={navigationNextRef}>
                            <FiChevronRight size={16} />
                        </div>
                    </div>
                </div>
            )}
            <div
                className={cn(styles.closeButtonWrapper, styles.noSwiping)}
                onClick={onClose}
                style={{ display: 'flex' }}
            >
                <div className={styles.closeButton} style={style.common}>
                    <FiX size={14} />
                </div>
            </div>

            {isLightbox && (
                <React.Suspense fallback={null}>
                    <LightBox
                        activeIndex={currentLightBoxSlideIndex}
                        images={photos.map((url) => ({ url }))}
                        onClose={closeLightBox}
                    />
                </React.Suspense>
            )}
        </div>
    );
};

ServicePhotos.propTypes = {
    photos: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    isActive: PropTypes.bool,
    isPrev: PropTypes.bool,
    isNext: PropTypes.bool,
    title: PropTypes.string.isRequired,
    themeStyles: PropTypes.object,
    style: PropTypes.object.isRequired,
    onClose: PropTypes.func,
};
